import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import {
  AccountBalanceWallet,
  Payment,
  Business,
  Person,
  Email,
  Phone,
  CheckCircle,
  Warning,
  Error,
  Info,
  Star,
  ExpandMore,
  Visibility,
  VisibilityOff,
  Security
} from "@mui/icons-material";
import client from "../../../../API";

const CreatorPaymentDialog = ({ open, onClose, creatorId, creatorName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (open && creatorId) {
      fetchCreatorPaymentInfo();
    }
  }, [open, creatorId]);

  const fetchCreatorPaymentInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await client.admin.getCreatorPaymentInfo(creatorId);
      setPaymentData(response);
    } catch (err) {
      console.error("Error fetching creator payment info:", err);
      setError(err.response?.data?.error || "Failed to load payment information");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentData(null);
    setError(null);
    onClose();
  };

  const getWalletIcon = (walletType) => {
    switch (walletType) {
      case 'paypal':
        return <Payment />;
      case 'stripe':
        return <Payment />;
      case 'wise':
        return <AccountBalanceWallet />;
      case 'bank_ach':
        return <AccountBalanceWallet />;
      case 'crypto':
        return <AccountBalanceWallet />;
      default:
        return <AccountBalanceWallet />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle />;
      case 'pending':
        return <Warning />;
      case 'rejected':
        return <Error />;
      default:
        return <Info />;
    }
  };

  const renderWalletDetails = (wallet) => {
    const details = [];
    
    if (wallet.wallet_type === 'paypal') {
      if (wallet.paypal_email) details.push({ label: 'PayPal Email', value: wallet.paypal_email });
      if (wallet.paypal_id) details.push({ label: 'PayPal ID', value: wallet.paypal_id });
    }
    
    if (wallet.wallet_type === 'stripe') {
      if (wallet.stripe_account_id) details.push({ label: 'Stripe Account ID', value: wallet.stripe_account_id });
      if (wallet.stripe_status) details.push({ label: 'Stripe Status', value: wallet.stripe_status });
      if (wallet.stripe_country) details.push({ label: 'Country', value: wallet.stripe_country });
    }
    
    if (wallet.wallet_type === 'wise') {
      if (wallet.wise_email) details.push({ label: 'Wise Email', value: wallet.wise_email });
      if (wallet.wise_profile_id) details.push({ label: 'Profile ID', value: wallet.wise_profile_id });
      if (wallet.wise_currency) details.push({ label: 'Currency', value: wallet.wise_currency });
      if (wallet.wise_recipient_id) details.push({ label: 'Recipient ID', value: wallet.wise_recipient_id });
    }
    
    if (wallet.wallet_type === 'bank_ach') {
      if (wallet.bank_name) details.push({ label: 'Bank Name', value: wallet.bank_name });
      if (wallet.bank_account_type) details.push({ label: 'Account Type', value: wallet.bank_account_type });
      if (wallet.bank_account_holder_name) details.push({ label: 'Account Holder', value: wallet.bank_account_holder_name });
      if (wallet.bank_routing_number) details.push({ label: 'Routing Number', value: wallet.bank_routing_number });
      if (wallet.bank_account_number) details.push({ label: 'Account Number', value: wallet.bank_account_number });
      if (wallet.bank_address) details.push({ label: 'Bank Address', value: wallet.bank_address });
      if (wallet.swift_code) details.push({ label: 'SWIFT Code', value: wallet.swift_code });
      if (wallet.iban) details.push({ label: 'IBAN', value: wallet.iban });
    }
    
    if (wallet.wallet_type === 'crypto') {
      if (wallet.crypto_currency) details.push({ label: 'Currency', value: wallet.crypto_currency });
      if (wallet.crypto_network) details.push({ label: 'Network', value: wallet.crypto_network });
      if (wallet.crypto_wallet_address) details.push({ label: 'Wallet Address', value: wallet.crypto_wallet_address });
      if (wallet.crypto_memo) details.push({ label: 'Memo/Tag', value: wallet.crypto_memo });
    }

    // Add common fields
    if (wallet.created_at) details.push({ label: 'Created', value: new Date(wallet.created_at).toLocaleString() });
    if (wallet.updated_at) details.push({ label: 'Updated', value: new Date(wallet.updated_at).toLocaleString() });
    if (wallet.last_used_at) details.push({ label: 'Last Used', value: new Date(wallet.last_used_at).toLocaleString() });
    
    return details;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Security />
          <Box>
            <Typography variant="h6">Complete Payment Information - Admin View</Typography>
            <Typography variant="body2" color="textSecondary">
              {creatorName || creatorId} - All sensitive data visible
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : paymentData ? (
          <Stack spacing={3}>
            {/* Security Notice */}
            <Alert severity="warning" icon={<Security />}>
              <Typography variant="body2">
                <strong>Admin Access:</strong> All payment details are displayed unmasked for administrative purposes. Handle this information securely.
              </Typography>
            </Alert>

            {/* Creator Basic Info */}
            <Card>
              <CardHeader 
                title="Creator Details" 
                avatar={<Person />}
                action={
                  <Chip 
                    label={paymentData.creator.status || 'Unknown'} 
                    color={paymentData.creator.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="textSecondary">Display Name</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {paymentData.creator.display_name || 'Not set'}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="textSecondary">Creator ID</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {paymentData.creator.creator_id}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="textSecondary">Contact Email</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {paymentData.creator.email || 'Not set'}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="textSecondary">Payout Email</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {paymentData.creator.payout_email || 'Not set'}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Legacy Payment Methods */}
            {(paymentData.creator.legacy_payout_preferred || paymentData.creator.legacy_stripe_id) && (
              <Card>
                <CardHeader 
                  title="Legacy Payment Methods" 
                  avatar={<Warning color="warning" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {paymentData.creator.legacy_payout_preferred && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="textSecondary">Legacy Preferred Payout</Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {paymentData.creator.legacy_payout_preferred}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {paymentData.creator.legacy_stripe_id && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="textSecondary">Legacy Stripe ID</Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {paymentData.creator.legacy_stripe_id}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Modern Wallets */}
            <Card>
              <CardHeader 
                title="Payment Wallets - Full Details" 
                avatar={<AccountBalanceWallet />}
                action={
                  <Chip 
                    label={paymentData.wallets.length > 0 ? `${paymentData.wallets.length} wallet(s)` : 'No wallets'}
                    color={paymentData.wallets.length > 0 ? 'success' : 'default'}
                    size="small"
                  />
                }
              />
              <CardContent>
                {paymentData.wallets.length === 0 ? (
                  <Alert severity="info">
                    No modern payment wallets configured. Creator may be using legacy payment methods.
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    {paymentData.wallets.map((wallet, index) => (
                      <Paper key={wallet.id} variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              {getWalletIcon(wallet.wallet_type)}
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                                    {wallet.wallet_type.replace('_', ' ')}
                                  </Typography>
                                  {wallet.is_primary && <Star color="warning" />}
                                </Stack>
                                {wallet.nickname && (
                                  <Typography variant="body2" color="textSecondary">
                                    {wallet.nickname}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                            <Chip 
                              icon={getStatusIcon(wallet.verification_status)}
                              label={wallet.verification_status || 'Unknown'}
                              color={getStatusColor(wallet.verification_status)}
                              size="medium"
                            />
                          </Stack>
                          
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Field</strong></TableCell>
                                  <TableCell><strong>Value</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {renderWalletDetails(wallet).map((detail, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontWeight: 'medium' }}>
                                      {detail.label}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      fontFamily: 'monospace', 
                                      fontSize: '0.875rem',
                                      wordBreak: 'break-all'
                                    }}>
                                      {detail.value}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {wallet.verification_notes && (
                            <Alert severity="info">
                              <Typography variant="body2">
                                <strong>Verification Notes:</strong> {wallet.verification_notes}
                              </Typography>
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Tax Information */}
            <Card>
              <CardHeader 
                title="Tax Information - Complete Details" 
                avatar={<Business />}
                action={
                  <Chip 
                    label={paymentData.tax_info ? 'Configured' : 'Not configured'}
                    color={paymentData.tax_info ? 'success' : 'default'}
                    size="small"
                  />
                }
              />
              <CardContent>
                {!paymentData.tax_info ? (
                  <Alert severity="info">
                    No tax information on file. Creator needs to complete tax setup for compliance.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Field</strong></TableCell>
                          <TableCell><strong>Value</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Legal Name</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.legal_name}
                          </TableCell>
                        </TableRow>
                        {paymentData.tax_info.business_name && (
                          <TableRow>
                            <TableCell>Business Name</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {paymentData.tax_info.business_name}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell>Tax ID Type</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', textTransform: 'uppercase' }}>
                            {paymentData.tax_info.tax_id_type}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tax ID Number</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 'bold' }}>
                            {paymentData.tax_info.tax_id_number}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Address Line 1</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.address_line1}
                          </TableCell>
                        </TableRow>
                        {paymentData.tax_info.address_line2 && (
                          <TableRow>
                            <TableCell>Address Line 2</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {paymentData.tax_info.address_line2}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell>City</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.city}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>State</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.state}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Postal Code</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.postal_code}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Country</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {paymentData.tax_info.country}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>W-9 Form Status</TableCell>
                          <TableCell>
                            <Chip 
                              label={paymentData.tax_info.w9_submitted ? 'Submitted' : 'Not submitted'}
                              color={paymentData.tax_info.w9_submitted ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>W-8 Form Status</TableCell>
                          <TableCell>
                            <Chip 
                              label={paymentData.tax_info.w8_submitted ? 'Submitted' : 'Not submitted'}
                              color={paymentData.tax_info.w8_submitted ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        {paymentData.tax_info.backup_withholding && (
                          <TableRow>
                            <TableCell>Backup Withholding</TableCell>
                            <TableCell>
                              <Chip 
                                label="Active"
                                color="warning"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        {paymentData.tax_info.verified_at && (
                          <TableRow>
                            <TableCell>Verification Date</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {new Date(paymentData.tax_info.verified_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Payment Setup Summary</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip 
                      label={paymentData.has_modern_setup ? 'Modern Setup' : 'Legacy Setup'}
                      color={paymentData.has_modern_setup ? 'success' : 'warning'}
                    />
                    <Chip 
                      label={`${paymentData.wallets.length} Wallet(s)`}
                      color={paymentData.wallets.length > 0 ? 'info' : 'default'}
                    />
                    <Chip 
                      label={paymentData.tax_info ? 'Tax Info Complete' : 'Tax Info Missing'}
                      color={paymentData.tax_info ? 'success' : 'warning'}
                    />
                    <Chip 
                      label="Full Admin Access"
                      color="error"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatorPaymentDialog;